import { supabase } from '../lib/supabase';
import { config } from '../config/config';
import { stripeService } from '../lib/stripe';
import { initiateCampayEdgePayment, checkCampayEdgePaymentStatus } from './campayEdgeService';
import { initiateMyCoolPayEdgePayment, checkMyCoolPayEdgePaymentStatus } from './mycoolpayEdgeService';
import { checkCampayShopEdgePaymentStatus } from './campayShopEdgeService';

interface PaymentDetails {
  amount: number;
  votes_count: number;
  paymentMethod: string;
  phoneNumber?: string;
  country?: string;
  operator?: string;
  contestantId: string;
  customerName?: string;
  customerSurname?: string;
  customerEmail?: string;
  description?: string;
}

interface CampayInitiateResponse {
  status: string;
  reference: string;
  payment_url: string;
  transaction: {
    amount: number;
    currency: string;
    reference: string;
    status: string;
  };
}

interface PayPalInitiateResponse {
  id: string;
  status: string;
  links: Array<{
    href: string;
    rel: string;
    method: string;
  }>;
}

const CAMPAY_API_URL = "https://campay.net/api";
export const PAYPAL_API_URL = "https://api.paypal.com";

// Mapper les méthodes de paiement
const PAYMENT_METHOD_MAPPING = {
  mtn: 'mobile_money',
  orange: 'mobile_money',
  paypal: 'card',
  stripe: 'card',
  plisio: 'crypto',
  monetbil: 'mobile_money',
  cinetpay: 'mobile_money',
  mycoolpay: 'mobile_money'
} as const;

// Fonction pour convertir le montant de XAF à EUR
const convertFCFAToEUR = (amountXAF: number): number => {
  // Taux de conversion : 500 XAF = 1 EUR
  const exchangeRate = 1 / 500;
  const amountEUR = amountXAF * exchangeRate;
  
  // Arrondir à 2 décimales et s'assurer que le montant est supérieur à 0
  const roundedAmount = Math.round((amountEUR + Number.EPSILON) * 100) / 100;
  console.log(`Conversion: ${amountXAF} XAF = ${roundedAmount} EUR (taux: ${exchangeRate})`);
  return roundedAmount;
};

// Fonction pour convertir le montant de XAF à USD
const convertFCFAToUSD = (amountXAF: number): number => {
  // Taux de conversion : 600 XAF = 1 USD
  const exchangeRate = 1 / 600;
  const amountUSD = amountXAF * exchangeRate;
  const roundedAmount = Math.round((amountUSD + Number.EPSILON) * 100) / 100;
  console.log(`Conversion: ${amountXAF} XAF = ${roundedAmount} USD (taux: ${exchangeRate})`);
  return roundedAmount;
};

// Initier un paiement avec Campay
const initiateCampayPayment = async (
  amount: number,
  phoneNumber: string,
  paymentMethod: string,
  transactionId: string,
  description: string
): Promise<{ reference: string; payment_url: string; transaction: any }> => { // Add explicit return type
  try {
    const response = await fetch(`${CAMPAY_API_URL}/collect/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Token ${config.campayAccessToken}`,
      },
      body: JSON.stringify({
        amount: amount,
        currency: 'XAF',
        from: phoneNumber,
        description: description,
        external_reference: transactionId,
        operator: paymentMethod.toUpperCase(),
        callback_url: config.campayCallbackUrl
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || "Échec de l'initiation du paiement");
    }

    const data: CampayInitiateResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Erreur lors de l'initiation du paiement Campay:", error);
    throw error;
  }
};

// Initier un paiement PayPal
const initiatePayPalPayment = async (
  amountXAF: number,
  transactionId: string
): Promise<{ approvalUrl: string; transactionId: string }> => { // Add explicit return type
  try {
    if (amountXAF <= 0) {
      throw new Error('Le montant doit être supérieur à 0');
    }

    // Convertir et vérifier le montant en EUR
    const amountEUR = convertFCFAToEUR(amountXAF);
    console.log('Montant à envoyer à PayPal:', amountEUR, 'EUR');

    if (amountEUR < 0.01) {
      throw new Error('Le montant minimum en EUR doit être de 0.01');
    }

    // Formater le montant avec exactement 2 décimales
    const formattedAmount = amountEUR.toFixed(2);

    // Obtenir un token d'accès
    const authResponse = await fetch(`${PAYPAL_API_URL}/v1/oauth2/token`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Accept-Language': 'en_US',
        'Authorization': `Basic ${btoa(`${config.paypalClientId}:${config.paypalSecret}`)}`,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: 'grant_type=client_credentials'
    });

    if (!authResponse.ok) {
      const authError = await authResponse.json();
      console.error('Erreur d\'authentification PayPal:', authError);
      throw new Error('Échec de l\'authentification PayPal');
    }

    const { access_token } = await authResponse.json();

    const orderData = {
      intent: 'CAPTURE',
      purchase_units: [{
        reference_id: transactionId,
        amount: {
          currency_code: 'EUR',
          value: formattedAmount,
          breakdown: {
            item_total: {
              currency_code: 'EUR',
              value: formattedAmount
            }
          }
        },
        items: [{
          name: 'Votes',
          description: `Paiement de votes - Transaction ${transactionId}`,
          quantity: '1',
          unit_amount: {
            currency_code: 'EUR',
            value: formattedAmount
          }
        }]
      }],
      application_context: {
        brand_name: 'VoteHub',
        landing_page: 'NO_PREFERENCE',
        shipping_preference: 'NO_SHIPPING',
        user_action: 'PAY_NOW',
        return_url: `${config.appUrl}/payment/success`,
        cancel_url: `${config.appUrl}/payment/cancel`
      }
    };

    console.log('Données de la commande PayPal:', JSON.stringify(orderData, null, 2));

    // Créer l'ordre PayPal
    const response = await fetch(`${PAYPAL_API_URL}/v2/checkout/orders`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${access_token}`,
        'PayPal-Request-Id': transactionId,
        'Prefer': 'return=representation'
      },
      body: JSON.stringify(orderData)
    });

    const responseData = await response.json();

    if (!response.ok) {
      console.error('Réponse d\'erreur PayPal complète:', responseData);
      if (responseData.details) {
        const errorDetails = responseData.details.map((d: any) => d.description || d.issue).join(', ');
        throw new Error(`Erreur PayPal: ${errorDetails}`);
      }
      throw new Error(responseData.message || "Échec de l'initiation du paiement PayPal");
    }

    console.log('Réponse PayPal réussie:', responseData);

    const data: PayPalInitiateResponse = responseData;
    const approvalUrl = data.links.find(link => link.rel === 'approve')?.href;

    if (!approvalUrl) {
      throw new Error("URL d'approbation PayPal non trouvée");
    }

    return {
      approvalUrl,
      transactionId: data.id
    };
  } catch (error) {
    console.error("Erreur lors de l'initiation du paiement PayPal:", error);
    throw error;
  }
};

// Initiate Plisio Payment by calling the Edge Function
const initiatePlisioPayment = async (
  amountXAF: number,
  transactionId: string,
  description: string,
  email?: string // Optional email for Plisio invoice
): Promise<{ invoice_url: string; invoice_id: string }> => { // Explicit return type
  try {
    const supabaseFunctionUrl = `${config.supabaseUrl}/functions/v1/plisio-proxy/create-invoice`;

    const response = await fetch(supabaseFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Pass Supabase Anon Key (public key) for function invocation
        'Authorization': `Bearer ${config.supabaseKey}`,
      },
      body: JSON.stringify({ 
        amountXAF,
        transactionId,
        description,
        email
       }),
    });

    const data = await response.json();

    if (!response.ok) {
      console.error("Error calling Plisio proxy function:", data);
      throw new Error(data.error || "Échec de l'initiation du paiement Plisio");
    }

    console.log('Plisio invoice created:', data);
    return { invoice_url: data.invoice_url, invoice_id: data.invoice_id };

  } catch (error) {
    console.error("Erreur lors de l'initiation du paiement Plisio:", error);
    throw error;
  }
};

// Initiate CinetPay Payment by calling the Edge Function
const initiateCinetPayPayment = async (
  amount: number,
  transactionId: string,
  description: string,
  customerInfo?: {
    name?: string;
    surname?: string;
    email?: string;
    phoneNumber?: string;
  }
): Promise<{ payment_url: string; transaction_id: string; payment_token: string }> => {
  try {
    const supabaseFunctionUrl = `${config.supabaseUrl}/functions/v1/cinetpay-proxy`;
    
    console.log('Initiating CinetPay payment:', { amount, transactionId });
    
    const response = await fetch(supabaseFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.supabaseKey}`,
      },
      body: JSON.stringify({ 
        amount,
        transactionId,
        description,
        customer_name: customerInfo?.name || '',
        customer_surname: customerInfo?.surname || '',
        customer_email: customerInfo?.email || '',
        customer_phone_number: customerInfo?.phoneNumber || ''
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('CinetPay payment initiation failed:', errorData);
      throw new Error(errorData.error || "Échec de l'initiation du paiement CinetPay");
    }
    
    const data = await response.json();
    console.log('CinetPay payment initiated successfully:', data);
    
    return {
      payment_url: data.payment_url,
      transaction_id: data.transaction_id,
      payment_token: data.payment_token
    };
  } catch (error) {
    console.error("Erreur lors de l'initiation du paiement CinetPay:", error);
    throw error;
  }
};

// Initiate Monetbil Payment by calling the Edge Function
const initiateMonetbilPayment = async (
  amount: number,
  phoneNumber: string | undefined,
  transactionId: string,
  description: string,
  country: string,
  operator: string,
  email?: string // Optional email
): Promise<{ payment_url: string; transaction_id: string }> => {
  try {
    const supabaseFunctionUrl = `${config.supabaseUrl}/functions/v1/monetbil-proxy`;
    
    console.log('Initiating Monetbil payment:', { amount, phoneNumber, transactionId });
    
    const response = await fetch(supabaseFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.supabaseKey}`,
      },
      body: JSON.stringify({ 
        amount,
        phoneNumber,
        transactionId,
        description,
        country,
        operator,
        email
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('Monetbil payment initiation failed:', errorData);
      throw new Error(errorData.error || "Échec de l'initiation du paiement Monetbil");
    }
    
    const data = await response.json();
    console.log('Monetbil payment initiated successfully:', data);
    
    return {
      payment_url: data.payment_url,
      transaction_id: data.transaction_id
    };
  } catch (error) {
    console.error("Erreur lors de l'initiation du paiement Monetbil:", error);
    throw error;
  }
};

// Mettre à jour les votes d'un candidat
const updateContestantVotes = async (contestantId: string, votes: number) => {
  try {
    // D'abord, obtenir le nombre actuel de votes
    const { data: contestant, error: selectError } = await supabase
      .from('contestants')
      .select('votes_count, contest_id')
      .eq('id', contestantId)
      .single();

    if (selectError) throw selectError;

    // Ensuite, mettre à jour avec le nouveau total
    const { error: updateError } = await supabase
      .from('contestants')
      .update({ 
        votes_count: (contestant?.votes_count || 0) + votes 
      })
      .eq('id', contestantId);

    if (updateError) throw updateError;

    // Insérer dans l'historique des votes
    const { error: historyError } = await supabase
      .from('contest_vote_history')
      .insert({
        contestant_id: contestantId,
        contest_id: contestant?.contest_id
      });

    if (historyError) throw historyError;
  } catch (error) {
    console.error('Erreur lors de la mise à jour des votes:', error);
    throw error;
  }
};

// Vérifier le statut d'un paiement Campay
export const checkCampayPaymentStatus = async (reference: string) => {
  try {
    const response = await fetch(`${CAMPAY_API_URL}/transaction/${reference}/`, {
      headers: {
        'Authorization': `Token ${config.campayAccessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error('Échec de la vérification du statut');
    }

    const data = await response.json();
    
    // Si le paiement est réussi, mettre à jour la transaction et les votes
    if (data.status === 'SUCCESSFUL') {
      // Récupérer les détails de la transaction
      const { data: transaction, error: transactionError } = await supabase
        .from('transactions')
        .select('contestant_id, votes_count, status')
        .eq('payment_reference', reference)
        .single();

      if (transactionError) throw transactionError;

      // Ne mettre à jour les votes que si la transaction n'a pas déjà été traitée
      if (transaction && transaction.status === 'pending') {
        // Mettre à jour le statut de la transaction
        const { error: updateError } = await supabase
          .from('transactions')
          .update({ status: 'completed' })
          .eq('payment_reference', reference);

        if (updateError) throw updateError;

        // Mettre à jour les votes du candidat
        await updateContestantVotes(transaction.contestant_id, transaction.votes_count);
      }
    }

    return data.status;
  } catch (error) {
    console.error('Erreur lors de la vérification du statut:', error);
    throw error;
  }
};

// Vérifier le statut d'un paiement Monetbil
export const checkMonetbilPaymentStatus = async (reference: string) => {
  try {
    const supabaseFunctionUrl = `${config.supabaseUrl}/functions/v1/monetbil-proxy/check-status`;
    
    const response = await fetch(supabaseFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.supabaseKey}`,
      },
      body: JSON.stringify({ 
        transaction_id: reference
      }),
    });

    if (!response.ok) {
      throw new Error('Échec de la vérification du statut Monetbil');
    }

    const data = await response.json();
    
    // Si le paiement est réussi, mettre à jour la transaction et les votes
    if (data.status === 'SUCCESSFUL' || data.status === 'SUCCESS') {
      // Récupérer les détails de la transaction
      const { data: transaction, error: txError } = await supabase
        .from('transactions')
        .select('contestant_id, amount, votes_count')
        .eq('payment_reference', reference)
        .single();

      if (txError) throw txError;

      if (transaction) {
        // Mettre à jour le statut de la transaction
        const { error: updateError } = await supabase
          .from('transactions')
          .update({ status: 'completed' })
          .eq('payment_reference', reference);

        if (updateError) throw updateError;

        // Mettre à jour les votes du candidat
        await updateContestantVotes(transaction.contestant_id, transaction.votes_count);
      }
    }

    return data.status;
  } catch (error) {
    console.error('Erreur lors de la vérification du statut Monetbil:', error);
    throw error;
  }
};

// Vérifier le statut d'un paiement CinetPay
const checkCinetPayPaymentStatus = async (reference: string) => {
  try {
    console.log('Vérification du statut du paiement CinetPay:', reference);
    
    const supabaseFunctionUrl = `${config.supabaseUrl}/functions/v1/cinetpay-proxy/status`;
    
    const response = await fetch(supabaseFunctionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.supabaseKey}`,
      },
      body: JSON.stringify({ 
        transaction_id: reference
      }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      console.error('Échec de la vérification du statut CinetPay:', errorData);
      throw new Error(errorData.error || "Échec de la vérification du statut");
    }
    
    const data = await response.json();
    console.log('Statut du paiement CinetPay récupéré:', data);
    
    return {
      status: data.status === 'ACCEPTED' ? 'success' : 
              data.status === 'PENDING' ? 'pending' : 
              'failed',
      data
    };
  } catch (error) {
    console.error("Erreur lors de la vérification du statut CinetPay:", error);
    throw error;
  }
};

// Fonction générique pour vérifier le statut d'un paiement selon la méthode
export const checkPaymentStatus = async (reference: string, paymentMethod: string) => {
  console.log(`[DEBUG][checkPaymentStatus] Début de la vérification - reference: ${reference}, paymentMethod: ${paymentMethod}`);
  
  try {
    if (!reference) {
      console.log(`[DEBUG][checkPaymentStatus] Référence de paiement manquante`);
      throw new Error('Référence de paiement manquante');
    }

    console.log(`[DEBUG][checkPaymentStatus] Vérification du statut pour ${paymentMethod}, référence: ${reference}`);

    if (["mtn", "orange"].includes(paymentMethod)) {
      // Vérifier si c'est un paiement pour la boutique (référence au format UUID)
      const isShopPayment = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(reference);
      
      if (isShopPayment) {
        console.log(`[DEBUG][checkPaymentStatus] Détection d'un paiement boutique (UUID), utilisation du service CampayShop pour vérifier le paiement ${reference}`);
        // Pour les paiements de la boutique, nous avons besoin de l'ID de commande
        // Essayons de le récupérer depuis la base de données
        const { data: order, error } = await supabase
          .from('shop_orders')
          .select('id')
          .eq('payment_reference', reference)
          .single();
          
        if (error || !order) {
          console.error(`[ERROR][checkPaymentStatus] Commande introuvable pour la référence ${reference}:`, error);
          throw new Error('Commande introuvable');
        }
        
        // Utiliser le service spécifique pour la boutique
        console.log(`[DEBUG][checkPaymentStatus] Appel à checkCampayShopEdgePaymentStatus avec reference=${reference}, order_id=${order.id}`);
        const result = await checkCampayShopEdgePaymentStatus(reference, order.id);
        console.log(`[DEBUG][checkPaymentStatus] Résultat de checkCampayShopEdgePaymentStatus:`, result);
        return result;
      } else {
        // Pour les paiements standards (non-boutique)
        console.log(`[DEBUG][checkPaymentStatus] Utilisation du service Campay standard pour vérifier le paiement ${reference}`);
        const result = await checkCampayEdgePaymentStatus(reference);
        console.log(`[DEBUG][checkPaymentStatus] Résultat de checkCampayEdgePaymentStatus:`, result);
        return result;
      }
    } else if (paymentMethod === 'monetbil') {
      console.log(`[DEBUG][checkPaymentStatus] Utilisation du service Monetbil pour vérifier le paiement ${reference}`);
      const result = await checkMonetbilPaymentStatus(reference);
      console.log(`[DEBUG][checkPaymentStatus] Résultat de checkMonetbilPaymentStatus:`, result);
      return result;
    } else if (paymentMethod === 'cinetpay') {
      console.log(`[DEBUG][checkPaymentStatus] Utilisation du service CinetPay pour vérifier le paiement ${reference}`);
      const result = await checkCinetPayPaymentStatus(reference);
      console.log(`[DEBUG][checkPaymentStatus] Résultat de checkCinetPayPaymentStatus:`, result);
      return result;
    } else if (paymentMethod === 'mycoolpay') {
      console.log(`[DEBUG][checkPaymentStatus] Utilisation du service My-CoolPay pour vérifier le paiement ${reference}`);
      // Pour My-CoolPay, nous utilisons l'ID de transaction stocké dans la base de données
      // car la référence externe peut être différente
      const { data: transaction, error } = await supabase
        .from('transactions')
        .select('id')
        .eq('payment_reference', reference)
        .single();
      
      if (error || !transaction) {
        console.log(`[DEBUG][checkPaymentStatus] Transaction non trouvée avec la référence: ${reference}`);
        console.log('Transaction non trouvée avec la référence:', reference);
        // Essayer avec l'ID directement
        return await checkMyCoolPayEdgePaymentStatus(reference);
      }
      
      return await checkMyCoolPayEdgePaymentStatus(transaction.id);
    } 
    // Ajouter d'autres méthodes ici si nécessaire
    
    // Par défaut, retourner un statut inconnu
    return { status: 'unknown', data: null };
  } catch (error) {
    console.error("Erreur lors de la vérification du statut:", error);
    throw error;
  }
};

// Vérifier la signature du webhook
export const verifyWebhookSignature = (signature: string, payload: string) => {
  const crypto = require('crypto');
  const hmac = crypto.createHmac('sha512', config.campayWebhookKey);
  hmac.update(payload);
  const calculatedSignature = hmac.digest('hex');
  
  return signature === calculatedSignature;
};

interface Transaction {
  id: string;
  amount: number;
  status: string;
  payment_method: string;
  payment_reference: string | null;
  created_at: string;
  updated_at: string;
  phone_number: string | null;
  external_reference: string | null;
  payment_data: any | null;
  contestant_id: string | null;
  payment_url: string | null;
}

interface PaymentInitiationResult {
  transaction: Transaction;
  payment_reference: string;
  payment_url?: string; // Optional, as not all methods return a URL immediately
}

// Create transaction record in DB
const createTransactionRecord = async (
  amount: number,
  dbPaymentMethod: string, // e.g., 'card', 'mobile_money', 'crypto'
  status: string,
  originalPaymentMethod: string, // e.g., 'stripe', 'plisio', 'mtn'
  phoneNumber: string | undefined,
  reference: string,
  paymentUrl: string | undefined,
  paymentData: any | null,
  contestantId: string,
  votes_count: number
): Promise<Transaction> => {
  const { data, error } = await supabase
    .from('transactions')
    .insert({
      amount,
      payment_method: dbPaymentMethod,
      status,
      phone_number: phoneNumber,
      payment_reference: reference,
      payment_url: paymentUrl,
      payment_data: paymentData,
      contestant_id: contestantId,
      votes_count
    })
    .select()
    .single();

  if (error) {
    console.error("Erreur lors de la création de la transaction:", error);
    throw error;
  }
  return data;
};

// Helper to get contestant details (name)
const getContestantDetails = async (contestantId: string): Promise<{ name: string | null } | null> => {
  if (!contestantId) return null;
  const { data, error } = await supabase
    .from('contestants')
    .select('name')
    .eq('id', contestantId)
    .single();
  
  if (error) {
     console.error("Error fetching contestant name:", error);
     return null; // Handle error gracefully
  }
  return data;
}

// Process payment initiation
export const processPayment = async (details: PaymentDetails): Promise<PaymentInitiationResult> => {
  try {
    // Generate a unique reference/ID for this payment attempt
    const attemptReference = `VOTE_${Math.random().toString(36).substring(2, 15).toUpperCase()}`;
    const description = `Vote pour ${await getContestantDetails(details.contestantId)?.then(d => d?.name || 'un candidat')} - ${details.votes_count} votes`;
    
    // Map the original payment method to a standardized category
    const dbPaymentMethod = details.paymentMethod in PAYMENT_METHOD_MAPPING 
      ? PAYMENT_METHOD_MAPPING[details.paymentMethod as keyof typeof PAYMENT_METHOD_MAPPING]
      : 'other';
    
    let transactionId: string;
    let initialTxData: Transaction;
    
    // Pour MTN et Orange, on ne crée PAS de transaction ici car la fonction Edge le fera
    if (["mtn", "orange"].includes(details.paymentMethod)) {
      // Appel à la fonction Edge sans créer de transaction préalable
      const campayResponse = await initiateCampayEdgePayment({
        amount: details.amount,
        votes_count: details.votes_count,
        phoneNumber: details.phoneNumber!,
        operator: details.paymentMethod,
        contestantId: details.contestantId,
        description: details.description || '',
        customerName: details.customerName,
        customerEmail: details.customerEmail,
      });

      return {
        transaction: campayResponse.transaction as unknown as Transaction,
        payment_reference: campayResponse.payment_reference,
        payment_url: campayResponse.payment_url,
      };
    }
    
    // Pour les autres méthodes de paiement, on continue à créer la transaction ici
    initialTxData = await createTransactionRecord(
      details.amount,
      dbPaymentMethod,
      'pending',
      details.paymentMethod,
      details.phoneNumber,
      attemptReference,
      undefined, // payment_url will be updated later
      { 
        country: details.country,
        operator: details.operator,
        customerName: details.customerName,
        customerEmail: details.customerEmail
      },
      details.contestantId,
      details.votes_count
    );
    
    transactionId = initialTxData.id;
    let paymentResponsePayload: { payment_reference: string; payment_url?: string } = { 
      payment_reference: attemptReference
    };
    
    if (details.paymentMethod === 'mycoolpay') {
      // Appel à la fonction Edge My-CoolPay
      const myCoolPayResponse = await initiateMyCoolPayEdgePayment({
        amount: details.amount,
        votes_count: details.votes_count,
        phoneNumber: details.phoneNumber!,
        operator: details.operator || 'MTN',
        contestantId: details.contestantId,
        description: description,
        customerName: details.customerName,
        customerEmail: details.customerEmail,
      });

      // Mettre à jour l'URL de paiement dans la base de données
      await supabase
        .from('transactions')
        .update({ 
          payment_url: myCoolPayResponse.payment_url,
          payment_reference: myCoolPayResponse.transaction_id
        })
        .eq('id', initialTxData.id);

      console.log('URL de paiement My-CoolPay:', myCoolPayResponse.payment_url);
      
      return {
        transaction: {
          ...initialTxData,
          payment_url: myCoolPayResponse.payment_url,
          payment_reference: myCoolPayResponse.transaction_id
        } as unknown as Transaction,
        payment_reference: myCoolPayResponse.transaction_id,
        payment_url: myCoolPayResponse.payment_url,
      };
    } else if (details.paymentMethod === 'paypal') {
      const paypalResponse = await initiatePayPalPayment(details.amount, transactionId);
      paymentResponsePayload = { payment_reference: paypalResponse.transactionId, payment_url: paypalResponse.approvalUrl };

    } else if (details.paymentMethod === 'stripe') {
      const stripeResponse = await stripeService.initializePayment({ amount: details.amount, reference: transactionId, description });
      paymentResponsePayload = { payment_reference: stripeResponse.reference, payment_url: stripeResponse.payment_url };

    } else if (details.paymentMethod === 'plisio') {
      // const userEmail = ...; // Get user email if needed
      const plisioResponse = await initiatePlisioPayment(details.amount, transactionId, description /*, userEmail */);
      paymentResponsePayload = { payment_reference: plisioResponse.invoice_id, payment_url: plisioResponse.invoice_url };
    
    } else if (details.paymentMethod === 'cinetpay') {
      const cinetpayResponse = await initiateCinetPayPayment(
        details.amount, 
        transactionId, 
        description,
        { 
          name: details.customerName,
          surname: details.customerSurname,
          email: details.customerEmail,
          phoneNumber: details.phoneNumber
        }
      );
      paymentResponsePayload = { 
        payment_reference: cinetpayResponse.transaction_id, 
        payment_url: cinetpayResponse.payment_url 
      };
    
    } else if (details.paymentMethod === 'monetbil') {
      if (!details.phoneNumber) throw new Error('Numéro de téléphone requis pour le paiement MTN Mobile Money');
      const monetbilResponse = await initiateMonetbilPayment(
        details.amount, 
        details.phoneNumber, 
        transactionId, 
        description, 
        details.country || 'CM', // Si country est undefined, utiliser CM pour Cameroun
        details.operator || 'CM_MTNMOBILEMONEY' // Si operator est undefined, utiliser MTN Cameroun
      );
      paymentResponsePayload = { payment_reference: monetbilResponse.transaction_id, payment_url: monetbilResponse.payment_url };
    
    } else {
      throw new Error('Méthode de paiement non supportée');
    }

    // Update the transaction with the specific payment reference and URL from the provider
    const { data: updatedTx, error: updateError } = await supabase
      .from('transactions')
      .update({ 
          payment_reference: paymentResponsePayload.payment_reference,
          payment_url: paymentResponsePayload.payment_url,
          external_reference: attemptReference // Keep the initial attempt reference if needed, or overwrite
       }) 
      .eq('id', transactionId)
      .select()
      .single();

    if (updateError) {
       console.error("Failed to update transaction with payment details:", updateError);
       // Decide how to handle: maybe return initial data, or throw
       throw updateError;
    }

    return {
      transaction: updatedTx || initialTxData, // Use updated data if available
      payment_reference: paymentResponsePayload.payment_reference,
      payment_url: paymentResponsePayload.payment_url,
    };

  } catch (error) {
    console.error("Erreur lors du traitement du paiement:", error);
    throw error;
  }
}
